import React from 'react';
import toast from 'react-hot-toast';
import ToggleSwitch from '@shared/ui/ToggleSwitch';
import Button from '@shared/ui/Button';
import { WarningIcon } from '../icons';
import { HelpTrigger } from '../../help';
import { useNewsFeedSelection } from '../../hooks/useNewsFeedSelection';

/**
 * News sources — which outlets appear in this user's news widget
 * (NEWS_FEEDS_ADMIN_PRD.md §5.10).
 *
 * The list is the admin catalog's enabled feeds. A user narrows it; they
 * cannot add a URL, because an arbitrary user-supplied feed is an unreviewed
 * scheduled outbound fetch from our backend.
 */
const NewsSourcesSection: React.FC = () => {
  const { feeds, effectiveIds, usingDefault, loading, error, saving, save } =
    useNewsFeedSelection();

  const toggle = (id: string) => {
    const next = effectiveIds.includes(id)
      ? effectiveIds.filter((feedId) => feedId !== id)
      : [...effectiveIds, id];

    // Selecting every feed and selecting none both mean "no preference" — the
    // empty array is how that is stored, and it keeps the user picking up
    // feeds added to the catalog later.
    save(next.length === feeds.length ? [] : next, {
      onError: () => toast.error('Could not save your news sources'),
    });
  };

  const reset = () => save([], { onError: () => toast.error('Could not reset your news sources') });

  return (
    <div className="card">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
          News Sources
        </h3>
        <HelpTrigger contentKey="profile-news-sources" size="sm" ariaLabel="Help for news sources" />
      </div>
      <p className="mb-6 text-text-secondary dark:text-text-dark-secondary">
        Choose which Colorado outlets appear in your news. Turn them all off and you&apos;ll see
        every source again — your news is never empty.
      </p>

      {loading && (
        <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
          Loading news sources...
        </div>
      )}

      {Boolean(error) && !loading && (
        <div className="flex items-center gap-3 rounded-md border-l-4 border-error-500 p-3">
          <WarningIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn&apos;t load news sources.
          </p>
        </div>
      )}

      {!loading && !error && feeds.length === 0 && (
        <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
          No news sources are available right now.
        </div>
      )}

      {!loading && !error && feeds.length > 0 && (
        <div className="space-y-4">
          {usingDefault && (
            <div className="rounded-md bg-neutral-100 px-3 py-2 text-sm text-text-secondary dark:bg-neutral-800 dark:text-text-dark-secondary">
              You&apos;re seeing all sources. Pick specific ones to narrow it down.
            </div>
          )}

          {feeds.map((feed) => (
            <div key={feed.id} className="card p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {feed.name}
                  </div>
                  <div className="mt-1 text-xs text-text-secondary dark:text-text-dark-secondary">
                    {feed.publisher || feed.region.replace(/-/g, ' ')}
                    {feed.description ? ` — ${feed.description}` : ''}
                  </div>
                </div>
                <ToggleSwitch
                  checked={effectiveIds.includes(feed.id)}
                  disabled={saving}
                  label={feed.name}
                  onChange={() => toggle(feed.id)}
                />
              </div>
            </div>
          ))}

          {!usingDefault && (
            <Button variant="outline" size="sm" onClick={reset} disabled={saving}>
              Show all sources
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsSourcesSection;
